import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { VoiceChangeInfo, VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { FormGroupDirective, ControlContainer, AbstractControl } from '@angular/forms';
import { Subscriber } from '@shared/models/subscriber.model';
import { getAgeLimitStatus } from '@sales/validators/age.validator';
import { Messages } from '@app/shared/enums/termination-messages';
import { PcmProduct } from '@app/sales/modules/subscription/models/pcm-product.model';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { AssignedSubscriptionModel } from '@app/shared/models/assigned-subscription-offering.model';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { Subscription } from 'rxjs';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { DatePipe } from '@angular/common';
import { UniqueSellingPointsComponent } from '@app/sales/modules/subscription/components/subscription-stepper/shared/unique-selling-points/unique-selling-points.component';
import { IcePillComponent } from '@app/shared/components/atoms/ice-pill/ice-pill.component';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { IceInfoIconComponent } from '@app/shared/components/atoms/ice-info-icon/ice-info-icon.component';
import { MatTooltip } from '@angular/material/tooltip';
import { IceRadioListItemComponent } from '@app/shared/components/atoms/ice-radio-list-item/ice-radio-list-item.component';

@Component({
  selector: 'app-vas-voice-offering-item',
  templateUrl: './vas-voice-offering-item.component.html',
  styleUrls: ['./vas-voice-offering-item.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceRadioListItemComponent,
    MatTooltip,
    IceInfoIconComponent,
    IceBadgeComponent,
    IcePillComponent,
    UniqueSellingPointsComponent,
    DatePipe,
    PriceFormatPipe,
  ],
})
export class VasVoiceOfferingItemComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);

  PriceType = PriceType;
  Messages = Messages;
  SubscriptionBindingExpirationDate = SubscriptionHelperService.subscriptionBindingExpirationDate;

  @Input() offering: VoiceSubscriptionOffering;
  @Input() changeInfo: VoiceChangeInfo;
  @Input() subscriber: Subscriber;
  @Input() assignedSubscription: AssignedSubscriptionModel = null;
  @Input() control: AbstractControl;
  @Input() isChangingSubscription: boolean;

  subscriberExceedsUpperAgeLimit: boolean;
  subscriberIsBelowLowerAgeLimit: boolean;

  subscription$: Subscription = null;

  get mobilePlanProduct(): PcmProduct {
    return OfferingHelperService.getMobilePlanProductFromOffering(this.offering);
  }

  get vasProduct(): PcmProduct {
    return OfferingHelperService.getVasProductFromOffering(this.offering);
  }

  get isDisabled(): boolean {
    return this.isChangingSubscription && (this.subscriberExceedsUpperAgeLimit || this.subscriberIsBelowLowerAgeLimit);
  }

  get tooltipMessage(): string {
    if (this.isChangingSubscription && (this.subscriberExceedsUpperAgeLimit || this.subscriberIsBelowLowerAgeLimit)) {
      const isAbove = this.subscriberExceedsUpperAgeLimit;
      const ageLimit = isAbove ? this.offering.upperAgeLimit : this.offering.lowerAgeLimit;
      const comparison = isAbove ? 'over' : 'under';

      return `Bruker er ${comparison} ${ageLimit} år og kan ikke kjøpe ${this.mobilePlanProduct?.productName}`;
    }

    return '';
  }

  ngOnInit() {
    this.subscription$ = this.control.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });
    const { exceedsUpperAgeLimit, isBelowLowerAgeLimit } = getAgeLimitStatus(this.subscriber?.birthday, this.offering);
    this.subscriberExceedsUpperAgeLimit = exceedsUpperAgeLimit;
    this.subscriberIsBelowLowerAgeLimit = isBelowLowerAgeLimit;
  }

  get existingBindingWillBeTransferred(): boolean {
    return !!(
      this.isChangingSubscription &&
      !this.changeInfo?.triggersBreakageFee &&
      this.SubscriptionBindingExpirationDate(this.assignedSubscription)
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
